/** Преобразование содержимого между классическим режимом и Components V2. */
import { newContainer, newMediaItem, newText, uid } from './factory.js';
import type { DContainer, DEmbed, DText, DV2Child, DV2Top, Draft } from './types.js';

function embedToContainer(e: DEmbed): DContainer {
  const c = newContainer();
  c.accent = e.color;
  c.children = [];
  const lines: string[] = [];
  if (e.author.name) lines.push(`-# ${e.author.url ? `[${e.author.name}](${e.author.url})` : e.author.name}`);
  if (e.title) lines.push(`### ${e.url ? `[${e.title}](${e.url})` : e.title}`);
  if (e.description) lines.push(e.description);
  const head: DText = newText(lines.join('\n') || '​');
  if (e.thumbnail) {
    c.children.push({ id: uid(), type: 'section', texts: [head], accessory: { type: 'thumbnail', url: e.thumbnail, description: '', spoiler: false } });
  } else if (lines.length) {
    c.children.push(head);
  }
  if (e.fields.length) {
    c.children.push(newText(e.fields.map((f) => `**${f.name}**\n${f.value}`).join('\n\n')));
  }
  if (e.image) c.children.push({ id: uid(), type: 'gallery', items: [newMediaItem(e.image)] });
  const footer = [e.footer.text, e.timestamp ? `<t:${Math.floor(Date.parse(e.timestamp) / 1000)}:f>` : ''].filter(Boolean).join(' • ');
  if (footer) {
    c.children.push({ id: uid(), type: 'separator', divider: true, spacing: 1 });
    c.children.push(newText(`-# ${footer}`));
  }
  if (!c.children.length) c.children.push(newText('​'));
  return c;
}

/** Классика -> V2: текст в текстовый блок, эмбеды в контейнеры, ряды как есть. */
export function classicToV2(d: Draft): Draft {
  const out: DV2Top[] = [];
  if (d.content.trim()) out.push(newText(d.content));
  for (const e of d.embeds) out.push(embedToContainer(e));
  for (const r of d.rows) out.push(r);
  const used = new Set<string>();
  const files = d.attachments.filter((a) => !/\.(png|jpe?g|gif|webp|mp4|webm|mov)$/i.test(a.name));
  const media = d.attachments.filter((a) => /\.(png|jpe?g|gif|webp|mp4|webm|mov)$/i.test(a.name));
  if (media.length) {
    out.push({ id: uid(), type: 'gallery', items: media.slice(0, 10).map((a) => ({ ...newMediaItem(`attachment://${a.name}`), spoiler: a.spoiler })) });
    media.forEach((a) => used.add(a.name));
  }
  for (const f of files) out.push({ id: uid(), type: 'file', attachment: f.name, spoiler: f.spoiler });
  return { ...d, mode: 'v2', content: '', embeds: [], rows: [], poll: null, v2: out };
}

function textsOf(list: DV2Child[]): string[] {
  const out: string[] = [];
  for (const c of list) {
    if (c.type === 'text') out.push(c.content);
    else if (c.type === 'section') out.push(...c.texts.map((t) => t.content));
  }
  return out;
}

/** V2 -> классика: контейнеры в эмбеды, текст вне контейнеров в content. */
export function v2ToClassic(d: Draft): Draft {
  const content: string[] = [];
  const embeds: DEmbed[] = [];
  const rows: Draft['rows'] = [];
  for (const top of d.v2) {
    if (top.type === 'container') {
      const image = top.children.find((c) => c.type === 'gallery');
      const section = top.children.find((c) => c.type === 'section');
      embeds.push({
        id: uid(),
        color: top.accent,
        title: '',
        url: '',
        description: textsOf(top.children).join('\n').slice(0, 4096),
        author: { name: '', url: '', iconUrl: '' },
        footer: { text: '', iconUrl: '' },
        timestamp: null,
        image: image && image.type === 'gallery' ? image.items[0]?.url ?? '' : '',
        thumbnail: section && section.type === 'section' && section.accessory.type === 'thumbnail' ? section.accessory.url : '',
        fields: [],
      });
      for (const c of top.children) if (c.type === 'row') rows.push(c);
    } else if (top.type === 'row') rows.push(top);
    else content.push(...textsOf([top]));
  }
  return { ...d, mode: 'classic', content: content.join('\n').slice(0, 2000), embeds: embeds.slice(0, 10), rows: rows.slice(0, 5), v2: [] };
}

export function isDraftEmpty(d: Draft): boolean {
  return d.mode === 'classic' ? !d.content.trim() && !d.embeds.length && !d.rows.length && !d.poll : d.v2.length === 0;
}
